import { useBreakpointValue } from "@chakra-ui/react";
import type { LanguageCode } from "config/languages";
import {
  companyMenu,
  imprintMenu,
  knowYourRightsMenu,
  legalMenu,
  productsMenu,
  supportMenu,
  topMenu,
  knowYourRightsFooterMenu,
} from "config/menus";
import type { MenuExtendedProps } from "config/menus";
import { AIR_PAYOUT_ID, IMPRINT_ID, OUR_FEES_ID, PRICE_LIST_ID } from "config/menus/menuPageId";
import { useServerSideStoryblokPages } from "contexts/DataContext/hooks";
import { getParentLanguage } from "utils/localeUtils";
import type { ResolvedMenuPagePaths } from "utils/menuUtils";
import { filterMenuByExcludedId, resolveMenuPagePaths } from "utils/menuUtils/menuUtils";
import { topMenuExperiment } from "modules/common/components/HeaderNav/experiment";

export const useResolveMenuPagePaths = (language: LanguageCode, menu: MenuExtendedProps[]): ResolvedMenuPagePaths[] => {
  const serverSideStoryblokPages = useServerSideStoryblokPages();
  const parentLanguage = getParentLanguage(language);

  return resolveMenuPagePaths(menu, language, parentLanguage, serverSideStoryblokPages);
};

export const useKnowYourRightsMenu = (language: LanguageCode, menuType: "topMenu" | "footerMenu" = "topMenu") => {
  const menu = menuType === "footerMenu" ? knowYourRightsFooterMenu : knowYourRightsMenu;
  return useResolveMenuPagePaths(language, menu);
};

export const useImprintMenu = (language: LanguageCode) => {
  let menu = imprintMenu;
  const supportedLanguages: LanguageCode[] = ["en", "de"];
  menu = supportedLanguages.includes(getParentLanguage(language)) ? menu : filterMenuByExcludedId(menu, IMPRINT_ID);

  return useResolveMenuPagePaths(language, menu);
};

export const useLegalMenu = (language: LanguageCode) => useResolveMenuPagePaths(language, legalMenu);

export const useProductsMenu = (language: LanguageCode) => useResolveMenuPagePaths(language, productsMenu);

export const useCompanyMenu = (language: LanguageCode) => useResolveMenuPagePaths(language, companyMenu);

export const useSupportMenu = (language: LanguageCode) => {
  const notTranslatedOurFeesPages: LanguageCode[] = ["pt-br"];

  let menu = supportMenu;

  menu = notTranslatedOurFeesPages.includes(language)
    ? filterMenuByExcludedId(menu, OUR_FEES_ID)
    : filterMenuByExcludedId(menu, PRICE_LIST_ID);

  return useResolveMenuPagePaths(language, menu);
};

export const useTopMenu = (language: LanguageCode) => {
  // // TODO | Remove after a new design is ready
  // // Hide the AirPayout link in top menu for for desktop (temporary solution, reason - it does not fit :)
  const isDesktop = useBreakpointValue({ lg: true }, { fallback: "lg" });
  const topMenuFiltered = isDesktop ? filterMenuByExcludedId(topMenu, AIR_PAYOUT_ID) : topMenu;

  return useResolveMenuPagePaths(language, topMenuFiltered);
};

export const useExperimentalTopMenu = (language: LanguageCode) => useResolveMenuPagePaths(language, topMenuExperiment);
